// One price lookup, for every kind of price.
//
// "One USD is worth 0.89 CHF" and "one VOO is worth 520 USD" are the same
// fact once fiat is an asset, so one structure answers both: a per-key,
// date-sorted array with a nearest-prior binary search over it. `fx.ts` and
// `prices.ts` are now views onto it rather than parallel implementations.
//
// Keeping them apart would cost more than duplication: a reader converting a
// fee would have to consult both and fall through from one to the other,
// because a fee paid in a coin is a "price" while one paid in a currency is a
// "rate" — a distinction with nothing behind it.
//
// Framework-free + tested so it runs identically on the server and the browser.

import { BASE_CURRENCY } from "@/finance/model/constants.js";

export interface MarketPrice {
  base: string; // what is being priced — "USD", "VOO", "DOGE"
  quote: string; // what it is priced in — "CHF", "USD", "BTC"
  asOf: string; // YYYY-MM-DD
  price: number; // quote units per 1 base unit
}

/** The assets a one-hop conversion may route through. See `priceAt`. */
const BRIDGES = [BASE_CURRENCY, "USD"] as const;

/** Key into the book. Uppercased so a lookup never depends on the caller's casing. */
const key = (base: string, quote: string): string => `${base.toUpperCase()}|${quote.toUpperCase()}`;

interface Series {
  dates: string[]; // ascending, so the lookup is a binary search
  prices: number[];
}

/** Every market's price history, plus the index needed to answer "what does
 *  this asset trade against?" without scanning the whole book. */
export interface PriceBook {
  series: Map<string, Series>;
  /** base symbol → quote symbols, most-observed first, then alphabetical. The
   *  ordering is what makes a single-argument lookup deterministic: an asset
   *  can have several markets in the real database, and a caller that asks
   *  only "what is it worth" must get the same answer on every run. */
  quotesFor: Map<string, string[]>;
}

/** One market's prices, oldest first, as the store holds them. */
export interface MarketSeries {
  base: string;
  quote: string;
  dates: string[];
  prices: number[];
}

const isPrice = (price: number): boolean => Number.isFinite(price) && price > 0;

/** The series of each day's price, the last observation of a day winning. */
function sorted(byDay: Map<string, number>): Series {
  const dates = [...byDay.keys()].sort();
  return { dates, prices: dates.map((d) => byDay.get(d)!) };
}

export function buildPriceBook(rows: readonly MarketPrice[]): PriceBook {
  const byMarket = new Map<string, Map<string, number>>();
  for (const r of rows) {
    if (!isPrice(r.price)) continue;
    const base = r.base.toUpperCase();
    const quote = r.quote.toUpperCase();
    if (base === quote) continue; // an asset is always worth one of itself
    const k = key(base, quote);
    let m = byMarket.get(k);
    if (!m) byMarket.set(k, (m = new Map()));
    m.set(r.asOf, r.price); // last write wins for a given day
  }
  return indexed(new Map([...byMarket].map(([k, m]) => [k, sorted(m)])));
}

/** The book from each market's prices, oldest first: the same book
 *  `buildPriceBook` makes of them, each series taken as it is rather than
 *  collected and sorted. */
export function priceBookOf(markets: readonly MarketSeries[]): PriceBook {
  const series = new Map<string, Series>();
  for (const m of markets) {
    const base = m.base.toUpperCase();
    const quote = m.quote.toUpperCase();
    if (base === quote) continue;
    const s = m.prices.every(isPrice)
      ? { dates: m.dates, prices: m.prices }
      : {
          dates: m.dates.filter((_, i) => isPrice(m.prices[i]!)),
          prices: m.prices.filter(isPrice),
        };
    if (s.dates.length === 0) continue;
    const k = key(base, quote);
    const held = series.get(k);
    // Two markets whose symbols differ only in case are one series.
    series.set(k, held ? sorted(new Map([...zip(held), ...zip(s)])) : s);
  }
  return indexed(series);
}

const zip = (s: Series): [string, number][] => s.dates.map((d, i) => [d, s.prices[i]!]);

/** The book over its series, with each base's markets most-observed first. */
function indexed(series: Map<string, Series>): PriceBook {
  const counts = new Map<string, { quote: string; n: number }[]>();
  for (const [k, s] of series) {
    const [base, quote] = k.split("|") as [string, string];
    const list = counts.get(base) ?? [];
    list.push({ quote, n: s.dates.length });
    counts.set(base, list);
  }

  const quotesFor = new Map<string, string[]>();
  for (const [base, list] of counts) {
    list.sort((a, b) => b.n - a.n || a.quote.localeCompare(b.quote));
    quotesFor.set(
      base,
      list.map((e) => e.quote),
    );
  }
  return { series, quotesFor };
}

export const EMPTY_PRICE_BOOK: PriceBook = { series: new Map(), quotesFor: new Map() };

/** Nearest-prior price on a series: the exact day if known, else the latest
 *  observation before it, else the earliest one there is. Never interpolates and
 *  never looks forward — a price from the future is a different kind of wrong
 *  from a price that is a few days stale. */
function at(series: Series | undefined, date: string): number | null {
  return observed(series, date)?.price ?? null;
}

/** The observation `at` answers with, and the day it was made. */
function observed(
  series: Series | undefined,
  date: string,
): { price: number; asOf: string } | null {
  if (!series || series.dates.length === 0) return null;
  const { dates, prices } = series;
  let lo = 0;
  let hi = dates.length - 1;
  let idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid]! <= date) {
      idx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  const i = idx >= 0 ? idx : 0; // before the first observation → earliest
  return { price: prices[i]!, asOf: dates[i]! };
}

/**
 * How many `quote` one `base` was worth on `date`, or `null` if the book cannot
 * say.
 *
 * Three routes, tried in order, and the order is the point — each is a weaker
 * claim than the one before it:
 *   1. the market itself;
 *   2. the inverse market, reciprocated (an EUR/CHF rate answers CHF/EUR);
 *   3. ONE hop through a hub asset — VOO→CHF via VOO/USD and CHF/USD, which is
 *      the app's single most common conversion and which no market in the book
 *      states directly.
 *
 * The hubs are the two assets this data actually revolves around, not an
 * arbitrary list: CHF because every reported figure is in it, and USD because
 * it quotes nearly every priced symbol and is the crypto cost-basis convention
 * (docs/portfolio.md).
 *
 * Exactly one hop, never a chain. Chaining would find a "price" for almost any
 * pair by multiplying together several stale observations, and a number
 * assembled that way is worse than no number: a caller can handle `null`
 * honestly, but cannot tell a solid figure from a compounded guess.
 */
export function priceAt(book: PriceBook, base: string, quote: string, date: string): number | null {
  const b = base.toUpperCase();
  const q = quote.toUpperCase();
  if (b === q) return 1;

  const direct = at(book.series.get(key(b, q)), date);
  if (direct != null) return direct;

  const inverse = at(book.series.get(key(q, b)), date);
  if (inverse != null && inverse !== 0) return 1 / inverse;

  for (const hub of BRIDGES) {
    if (hub === b || hub === q) continue;
    const baseInHub = priceAtNoBridge(book, b, hub, date);
    if (baseInHub == null) continue;
    const quoteInHub = priceAtNoBridge(book, q, hub, date);
    if (quoteInHub != null && quoteInHub !== 0) return baseInHub / quoteInHub;
  }
  return null;
}

/** Steps 1 and 2 only — the bridge's own legs must not themselves be bridged. */
function priceAtNoBridge(
  book: PriceBook,
  base: string,
  quote: string,
  date: string,
): number | null {
  if (base === quote) return 1;
  const direct = at(book.series.get(key(base, quote)), date);
  if (direct != null) return direct;
  const inverse = at(book.series.get(key(quote, base)), date);
  return inverse != null && inverse !== 0 ? 1 / inverse : null;
}

/** What an asset is worth without the caller naming a quote — the asset's
 *  most-observed market. Deterministic by construction (see `quotesFor`), and
 *  it reports which quote it answered in, because for DOGE that is genuinely
 *  ambiguous and a bare number would hide it. */
export function priceOf(
  book: PriceBook,
  base: string,
  date: string,
): { price: number; quote: string; asOf: string } | null {
  const quotes = book.quotesFor.get(base.toUpperCase());
  if (!quotes || quotes.length === 0) return null;
  for (const quote of quotes) {
    const found = observed(book.series.get(key(base, quote)), date);
    if (found != null) return { price: found.price, quote, asOf: found.asOf };
  }
  return null;
}

/** Convert an amount between two assets at `date`'s price. `null` when the book
 *  cannot price the pair — never a silent 1:1, which would report a foreign
 *  amount as if it were already in the target currency. */
export function convert(
  amount: number | null | undefined,
  from: string | null | undefined,
  to: string,
  date: string,
  book: PriceBook,
): number | null {
  if (amount == null || !Number.isFinite(amount) || !from) return null;
  const rate = priceAt(book, from, to, date);
  return rate == null ? null : amount * rate;
}
